import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import type { McGrawHillActionsBlockPayload } from '@/types/chatBlocks';

/** How long Copy answer reports success. */
const COPIED_MS = 1500;

/** The action payload, or null when it is not a usable one. */
export const parseMcGrawHillActionsPayload = (
  payload: Record<string, unknown>
): McGrawHillActionsBlockPayload | null => {
  const { open, copyText } = payload;
  if (typeof copyText !== 'string') return null;
  if (!open || typeof open !== 'object') return { open: null, copyText };

  const { label, documentId, page, highlightText } = open as Record<
    string,
    unknown
  >;

  const isOpenValid =
    typeof label === 'string' &&
    !!label &&
    typeof documentId === 'string' &&
    !!documentId &&
    typeof page === 'number' &&
    Number.isSafeInteger(page) &&
    page >= 1;

  return {
    open: isOpenValid
      ? {
          label,
          documentId,
          page,
          ...(typeof highlightText === 'string' && highlightText
            ? { highlightText }
            : {}),
        }
      : null,
    copyText,
  };
};

/**
 * mcgraw-hill: a scripted answer's action row: open the reference document
 * the answer rests on, and copy the answer.
 */
const McGrawHillActions = ({
  payload,
}: {
  payload: McGrawHillActionsBlockPayload;
}) => {
  const { open } = useChatDocumentViewer();
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard?.writeText(payload.copyText);
      setIsCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setIsCopied(false), COPIED_MS);
    } catch {
      // Clipboard refused (permissions): nothing to report in a demo row.
    }
  }, [payload.copyText]);

  const target = payload.open;

  return (
    <div className="mcgraw-hill-actions">
      {target ? (
        <button
          type="button"
          className="mcgraw-hill-actions__primary"
          onClick={() =>
            open(
              { kind: 'chatDocument', documentId: target.documentId },
              {
                page: target.page,
                ...(target.highlightText
                  ? { highlightText: target.highlightText }
                  : {}),
              }
            )
          }
        >
          {target.label}
        </button>
      ) : null}
      <button
        type="button"
        className="mcgraw-hill-actions__copy"
        onClick={() => void handleCopy()}
      >
        {isCopied ? 'Copied' : 'Copy answer'}
      </button>
    </div>
  );
};

export default McGrawHillActions;
